'use client';

import { useState, useEffect } from 'react';
import { Environment } from '@/libs/Environment';

/**
 * Partner key type based on Environment.partnerships keys
 */
export type PartnerKey = keyof typeof Environment.partnerships;

/**
 * Partner configuration from Environment.ts
 */
export type PartnerConfig = (typeof Environment.partnerships)[PartnerKey];

/**
 * Return type for usePartner hook
 */
export interface UsePartnerResult {
  /** The detected partner key (e.g., 'mtn', 'telecel', 'motito') */
  partnerKey: PartnerKey;
  /** The full partner configuration object */
  partner: PartnerConfig;
  /** Whether partner detection is still in progress (for SSR hydration) */
  isLoading: boolean;
  /** Whether the current domain is a partner-specific domain (not generic motito) */
  isPartnerDomain: boolean;
}

function normalizePartnerCookieValue(value: string | null): PartnerKey | null {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === 'vodafone') return 'telecel';
  if (normalized in Environment.partnerships) {
    return normalized as PartnerKey;
  }
  return null;
}

function readCookieValue(cookieString: string, name: string): string | null {
  const encodedName = `${encodeURIComponent(name)}=`;
  const match = cookieString
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(encodedName));
  if (!match) return null;
  return decodeURIComponent(match.slice(encodedName.length));
}

export function resolvePartnerKeyFromBrowserContext(params: {
  search: string;
  hostname: string;
  cookieString: string;
}): PartnerKey {
  const urlParams = new URLSearchParams(params.search);
  const partnerOverride = normalizePartnerCookieValue(urlParams.get('partner'));
  if (partnerOverride) {
    return partnerOverride;
  }

  const partnerFromCookie = normalizePartnerCookieValue(
    readCookieValue(params.cookieString, 'partner_code')
  );
  if (partnerFromCookie) {
    return partnerFromCookie;
  }

  const matchedKey = (Object.keys(Environment.partnerships) as PartnerKey[]).find(
    (key) => {
      if (key === 'motito') return false;
      return params.hostname.includes(key);
    }
  );

  return matchedKey || 'motito';
}

/**
 * Hook for detecting the current partner based on hostname.
 *
 * Design decisions:
 * 1. SSR Safe: Defaults to 'motito' during SSR (no window object)
 * 2. Hydration: Uses isLoading state to prevent flash of wrong content
 * 3. Detection: Checks if hostname includes partner key (flexible matching)
 * 4. Fallback: Returns 'motito' for generic/unknown domains
 *
 * Usage:
 * ```tsx
 * const { partnerKey, partner, isLoading, isPartnerDomain } = usePartner();
 *
 * if (isLoading) return <Loading />;
 *
 * // Only show partner banner on partner domains
 * if (!isPartnerDomain) return null;
 *
 * return <Banner color={partner.primaryColor} />;
 * ```
 */
export function usePartner(): UsePartnerResult {
  const [partnerKey, setPartnerKey] = useState<PartnerKey>('motito');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Only run on client
    if (typeof window === 'undefined') {
      setIsLoading(false);
      return;
    }

    setPartnerKey(
      resolvePartnerKeyFromBrowserContext({
        search: window.location.search,
        hostname: window.location.hostname.toLowerCase(),
        cookieString: document.cookie,
      })
    );
    setIsLoading(false);
  }, []);

  // Get partner config directly - no need for memoization of simple object lookup
  const partner = Environment.partnerships[partnerKey];

  // Check if this is a partner-specific domain (not generic motito marketplace)
  const isPartnerDomain = partnerKey !== 'motito';

  return {
    partnerKey,
    partner,
    isLoading,
    isPartnerDomain,
  };
}
